import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import swal from 'sweetalert2';
import qs from 'qs';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { Document, Page, BlobProvider } from '@react-pdf/renderer';
import Content from '../components/pdf/Content';
import { pdfStyle } from '../styles/pdfStyle';
import { useUserState } from '../contexts/UserContext';
import Input from '../components/common/Input';
import TextBox from '../components/common/TextBox';
import Loading from '../components/common/Loading';

const Container = styled.div`
  width: 100%;
  max-width: 960px;
  margin: 0 auto;
  padding: 40px 20px;
  display: flex;
  gap: 40px;
  @media (max-width: 768px) {
    flex-direction: column;
    padding: 20px 16px;
    gap: 24px;
  }
`;

const LeftCol = styled.div`
  flex: 1;
  min-width: 0;
`;

const RightCol = styled.div`
  width: 340px;
  flex-shrink: 0;
  @media (max-width: 768px) {
    width: 100%;
  }
`;

const SectionTitle = styled.h3`
  font-size: 18px;
  font-weight: 700;
  margin: 0 0 16px 0;
`;

const Card = styled.div`
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 14px;
  padding: 24px;
  margin-bottom: 20px;
`;

const ItemRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 0;
  border-bottom: 1px solid #f3f4f6;
  &:last-child { border-bottom: none; }
`;

const ItemName = styled.div`
  font-size: 14px;
  font-weight: 500;
  flex: 1;
  margin-right: 12px;
`;

const ItemDetail = styled.div`
  font-size: 13px;
  color: #666;
  white-space: nowrap;
`;

const SummaryRow = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 8px 0;
  font-size: 14px;
  color: ${({ $bold }) => ($bold ? '#111' : '#555')};
  font-weight: ${({ $bold }) => ($bold ? '700' : '400')};
  font-size: ${({ $bold }) => ($bold ? '16px' : '14px')};
  ${({ $border }) => $border && 'border-top: 1px solid #e5e7eb; padding-top: 12px; margin-top: 4px;'}
`;

const OrderBtn = styled.button`
  width: 100%;
  padding: 16px;
  border: none;
  border-radius: 12px;
  background: #111;
  color: #fff;
  font-size: 16px;
  font-weight: 700;
  cursor: pointer;
  transition: background 0.15s;
  &:hover { background: #333; }
  &:disabled { background: #ccc; cursor: not-allowed; }
`;

const EmptyMsg = styled.div`
  text-align: center;
  padding: 40px;
  color: #999;
  font-size: 15px;
`;

const convertBlobToBase64 = (blob) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = reject;
    reader.onload = () => resolve(reader.result);
    reader.readAsDataURL(blob);
  });

const OrderPage = ({ onSelect }) => {
  const { user } = useUserState();
  const cart = useSelector((store) => store.cartReducer);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [shipping, setShipping] = useState(0);

  const [inputs, setInputs] = useState({
    type: 'inputs',
    name: user.manager_name || '',
    display: true,
    phoneNo: user.manager_contact || '',
    address: '',
    other: '',
  });

  useEffect(() => {
    axios.get('api/shipping/price').then(({ data }) => setShipping(Number(data))).catch(() => {});
    // 마지막 배송지 불러오기
    axios.get(`/api/orders/orderlist/last-shipping/${user._id}`).then(({ data }) => {
      if (data.shipping_address) {
        setInputs((prev) => ({
          ...prev,
          name: data.shipping_name || prev.name,
          phoneNo: data.shipping_phone || prev.phoneNo,
          address: data.shipping_address || prev.address,
        }));
      }
    }).catch(() => {});
  }, []);

  const sum = cart ? cart.reduce((s, item) => s + item.price, 0) : 0;
  const actualShipping = sum >= 100000 ? 0 : shipping;
  const total = sum + actualShipping;

  const handleChange = (e) => {
    const { value, name } = e.target;
    setInputs({ ...inputs, [name]: value });
  };

  const handleOrder = (blob) => {
    if (!cart || cart.length === 0) return;
    swal.fire({
      text: '주문하시겠습니까?',
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#111',
      cancelButtonColor: '#a8a8a8',
      confirmButtonText: '주문 확인',
      cancelButtonText: '취소',
    }).then(async (result) => {
      if (!result.isConfirmed) return;
      try {
        setIsLoading(true);
        const b64 = await convertBlobToBase64(blob);
        const cartItemsForServer = cart.map((item) => ({
          product_name: item.product_name,
          count: item.count,
        }));
        const data = qs.stringify({
          user_id: user._id,
          company_name: user.company_name,
          pdfUrl: b64,
          price: sum,
          cartItems: JSON.stringify(cartItemsForServer),
          shipping_fee: actualShipping,
          shipping_name: inputs.name,
          shipping_phone: inputs.phoneNo,
          shipping_address: inputs.address,
        });
        const res = await axios.post('/api/orders/orderlist', data, {
          headers: { 'Content-type': 'application/x-www-form-urlencoded' },
        });
        if (res.status === 200) {
          const order_id = res.data.order.order_id;
          // 이메일 발송 (실패해도 주문은 완료)
          try {
            await axios.post('/mail', {
              user_email: user.user_email,
              manager_name: user.manager_name,
              company_name: user.company_name,
              manager_contact: user.manager_contact,
              download: b64,
              order_id,
            });
          } catch (emailErr) {
            console.log('이메일 발송 오류', emailErr);
          }
          dispatch({ type: 'CLEAR_CART' });
          setIsLoading(false);
          swal.fire({
            title: '주문이 완료되었습니다!',
            text: '발주서는 마이페이지에서 확인 및 다운로드가 가능합니다.',
            icon: 'success',
            confirmButtonColor: '#111',
          }).then(() => {
            navigate('/mypage');
            if (onSelect) onSelect('MYPAGE');
          });
        }
      } catch (e) {
        setIsLoading(false);
        const errMsg = e.response?.data?.message || '오류가 발생했습니다.';
        swal.fire({
          text: errMsg,
          icon: 'error',
          confirmButtonColor: '#a8a8a8',
        }).then(() => {
          if (e.response?.status === 400 && e.response?.data?.currentLevel) {
            window.location.reload();
          }
        });
      }
    });
  };

  if (!cart || cart.length === 0) {
    return (
      <Container>
        <EmptyMsg>
          장바구니가 비어있습니다.<br />
          상품을 먼저 담아주세요.
        </EmptyMsg>
      </Container>
    );
  }

  const pdfDoc = (
    <Document language="kr">
      <Page size="A4" style={pdfStyle.page}>
        <Content user={user} inputs={inputs} cart={cart} sum={sum} shipping={actualShipping} />
      </Page>
    </Document>
  );

  return (
    <>
      <Container>
        <LeftCol>
          <SectionTitle>주문 상품</SectionTitle>
          <Card>
            {cart.map((item, i) => (
              <ItemRow key={i}>
                <ItemName>{item.product_name}</ItemName>
                <ItemDetail>
                  {item.count}개 &times; {(item.price / item.count).toLocaleString('ko-KR')}원 = {item.price.toLocaleString('ko-KR')}원
                </ItemDetail>
              </ItemRow>
            ))}
          </Card>

          <SectionTitle>배송 정보</SectionTitle>
          <Card>
            <Input type="name" name="name" value={inputs.name} onChange={handleChange} placeholder="수령인" />
            <Input name="phoneNo" value={inputs.phoneNo} onChange={handleChange} placeholder="연락처" />
            <Input name="address" value={inputs.address} onChange={handleChange} placeholder="주소" />
            <TextBox name="other" value={inputs.other} onChange={handleChange} placeholder="비고" height="100px" />
          </Card>
        </LeftCol>

        <RightCol>
          <SectionTitle>주문 요약</SectionTitle>
          <Card>
            <SummaryRow>
              <span>상품 금액</span>
              <span>{sum.toLocaleString('ko-KR')}원</span>
            </SummaryRow>
            <SummaryRow>
              <span>배송비</span>
              <span>{actualShipping > 0 ? actualShipping.toLocaleString('ko-KR') + '원' : '무료'}</span>
            </SummaryRow>
            <SummaryRow $bold $border>
              <span>총 결제 금액</span>
              <span>{total.toLocaleString('ko-KR')}원</span>
            </SummaryRow>
          </Card>

          <BlobProvider document={pdfDoc}>
            {({ blob, loading: pdfLoading }) => (
              <OrderBtn
                disabled={pdfLoading || isLoading}
                onClick={() => handleOrder(blob)}
              >
                {pdfLoading ? '발주서 준비 중...' : '주문하기'}
              </OrderBtn>
            )}
          </BlobProvider>
        </RightCol>
      </Container>
      {isLoading && <Loading />}
    </>
  );
};

export default OrderPage;
